
import ymaps from 'ymaps'
import { mapState } from 'pinia'
import 'leaflet/dist/leaflet.css'
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css'
import { useResizeStore } from '~/store/resize'

export default {
  name: 'DeliveryMap',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /**
     * Массив маркеров, которые нужно поставить на карту, формат у каждого маркера такой же, как у пропса value
     */
    list: {
      type: Array,
      default: () => [],
    },
    /**
     * Можно ли выбирать на карте любое место и на этом месте создавать новый маркер
     */
    allowSelectAny: {
      type: Boolean,
      default: false,
    },
    /**
     * Текущее выбранное значение в формате:
     * {
     *  value: значение, будет использовать как уникальный идентификатор,
     *  gpsN: кординаты, по другому это lat,
     *  gpsS: кординаты, по другому это lng,
     *  ...любые другие свойства, будут пробрасываться наверх при выборе пункта на карте
     * }
     */
    value: {
      type: Object,
      default: () => ({}),
    },
    center: {
      type: Array,
      default() {
        return [42.968548, 47.495564]
      },
    },
    bounds: {
      type: Array,
      default: null,
    },
    zone: {
      type: Array,
      default: null,
    },
    zoom: {
      type: Number,
      default: 12,
    },
    zoomValueOnMarkerClick: {
      type: Number,
      default: 16,
    },
    height: {
      type: String,
      default: null,
    },
    isNotScroll: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      innerCenter: this.center,
      iconDefault: null,
      iconSelected: null,
      selected: null,
      polygon: null,
      markers: [],
    }
  },
  computed: {
    ...mapState(useResizeStore, {
      isMobile: store => store.isMobile,
    }),
  },
  watch: {
    center(val) {
      if (this.map) {
        this.innerCenter = this.getArray(val?.[0], val?.[1])
        this.map.setView(this.innerCenter)
      }
    },
    value: {
      deep: true,
      handler(val) {
        if (this.map && val?.lat && val?.lon) {
          this.map.setView([val?.lat, val?.lon])
          this.refreshMarkerLayer()
        }
      },
    },
    list() {
      if (this.map)
        this.refreshMarkerLayer()
    },
  },
  async mounted() {
    this.L = require('leaflet')
    require('./wise-leaflet-pip.js')
    await ymaps.load(`https://api-maps.yandex.ru/2.1/?lang=ru_RU&apikey=${process.env.yandexMap}`)
    require('leaflet-gesture-handling')
    require('./yandex.js')

    window.requestAnimationFrame(() => {
      this.map = this.createMap()
      if (!this.map)
        return

      this.createLayer(this.map)
      this.createMarkers()
      this.refreshMarkerLayer()
      if (this.zone) {
        this.polygon = this.L.polygon(this.zone, { color: '#30C63F' }).addTo(this.map)
        this.map.fitBounds(this.polygon.getBounds())
      }
      this.map.on('click', this.onMapClick)
    })
  },
  destroyed() {
    if (this.map) {
      this.map.off()
      this.map.remove()
    }
  },
  methods: {
    createMarkers() {
      this.iconDefault = this.L.icon({
        iconUrl: require('@/assets/images/delivery-map/marker-default.svg'),
        iconSize: [34, 55],
        iconAnchor: [17, 52],
      })

      this.iconSelected = this.L.icon({
        iconUrl: require('@/assets/images/delivery-map/marker-selected.svg'),
        iconSize: [34, 55],
        iconAnchor: [17, 52],
      })
    },
    createLayer() {
      return this.L.yandex().addTo(this.map)

      // Open Street Map
      // return this.L.tileLayer('http://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png').addTo(this.map)
    },
    refreshMarkerLayer() {
      const oldMarkers = this.markers
      this.markers = []
      let markers = []

      if (this.list && this.list.length)
        markers = [...this.list]

      if (this.value?.lat && this.value?.lon) {
        markers = [...markers, this.value]
        this.selected = this.value.id
      }

      markers.forEach((coord) => {
        const isSelected = coord.id === this.selected
        const marker = this.L.marker([coord?.lat, coord?.lon], {
          icon: isSelected ? this.iconSelected : this.iconDefault,
        })
          .addTo(this.map)
          .on('click', (event) => {
            this.onMarkerClick(coord, event)
          })

        this.markers.push(marker)
      })

      oldMarkers.forEach(m => m.remove())
    },
    createMap() {
      if (this.$refs?.map) {
        return this.L.map(this.$refs.map, {
          attributionControl: false,
          gestureHandling: this.isMobile && this.isNotScroll,
          zoom: this.zoom,
          bounds: this.bounds,
          center: this.innerCenter,
        })
      }
    },
    onMarkerClick(coord) {
      this.map.setView([coord?.lat, coord?.lon], this.zoomValueOnMarkerClick)
      this.$emit('change', coord)
    },
    async onMapClick({ latlng } = {}) {
      if (this.allowSelectAny) {
        try {
          const address = await this.$api.geo.getAddressByCoordinate({ count: 1, lat: latlng.lat, lon: latlng.lng })
          this.$emit('change', address, {
            allow: this?.polygon?.contains(latlng),
          })
        }
        catch (error) {
          new this.$baseError({
            message: 'Не удалось получить подсказки к запросу через яндекс',
            native: error,
          })
          try {
            const address = await this.$api.geo.getAddress({ count: 1, ...latlng })
            this.$emit('change', address, {
              allow: this?.polygon?.contains(latlng),
            })
          }
          catch (e) {
            new this.$baseError({
              message: 'Не удалось получить подсказки к запросу через дадату',
              native: e,
            })
          }
        }
      }
    },
    centerUpdated(updatedCenter) {
      this.$emit('centerUpdated', updatedCenter)
    },
    getArray(val, val2) {
      val = typeof val === 'number' ? val : 0
      val2 = typeof val2 === 'number' ? val2 : 0
      return [val, val2]
    },
  },
}
